import { apiFetch } from "../lib/api";

export type BackfillSource = "delta" | "bhavcopy" | "zerodha" | "dhan" | "dukascopy";
export type BackfillStatus =
  | "PENDING"
  | "RUNNING"
  | "SUCCESS"
  | "FAILED"
  | "CANCELLED";

export interface BackfillJob {
  id: string;
  user_id: string;
  source: BackfillSource;
  broker_connection_id: string | null;
  symbol: string;
  exchange: string;
  instrument_type: string;
  expiry: string | null;
  strike: string | null;
  lot_size: number;
  tick_size: string;
  timeframe: string;
  start_ts: string;
  end_ts: string;
  synthetic_volume_from_futures: boolean;
  synthetic_volume_from_constituents: boolean;
  status: BackfillStatus;
  rows_written: number | null;
  error_message: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BackfillCreate {
  source: BackfillSource;
  broker_connection_id?: string | null;
  symbol: string;
  exchange: string;
  instrument_type?: string;
  expiry?: string | null;
  strike?: string | null;
  lot_size?: number;
  tick_size?: string;
  timeframe: string;
  start: string; // ISO
  end: string;
  synthetic_volume_from_futures?: boolean;
  synthetic_volume_from_constituents?: boolean;
}

export interface CoverageRow {
  exchange: string;
  symbol: string;
  instrument_type: string;
  expiry: string | null;
  strike: string | null;
  timeframe: string;
  bar_count: number;
  first_ts: string | null;
  last_ts: string | null;
}

export async function listCoverage(): Promise<CoverageRow[]> {
  return apiFetch<CoverageRow[]>("/data-coverage");
}

export async function listBackfills(limit = 50): Promise<BackfillJob[]> {
  return apiFetch<BackfillJob[]>(`/backfills?limit=${limit}`);
}

export async function getBackfill(id: string): Promise<BackfillJob> {
  return apiFetch<BackfillJob>(`/backfills/${id}`);
}

export interface GapFillRequest {
  symbol: string;
  exchange: string;
  instrument_type?: string;
  expiry?: string | null;
  strike?: string | null;
  lot_size?: number;
  tick_size?: string;
  timeframe: string;
}

export interface GapFillResponse {
  jobs: BackfillJob[];
  priority_job_id: string | null;
  detail: string | null;
}

export async function createGapFill(payload: GapFillRequest): Promise<GapFillResponse> {
  return apiFetch<GapFillResponse>("/backfills/gap-fill", {
    method: "POST",
    body: payload,
  });
}

export async function createBackfill(payload: BackfillCreate): Promise<BackfillJob> {
  return apiFetch<BackfillJob>("/backfills", { method: "POST", body: payload });
}

/** Canonical timeframe list, coarsest first — the order the backend schedules
 *  in, and the order the chart fills them in. */
export const ALL_TIMEFRAMES = [
  "1d", "1h", "30m", "15m", "10m", "5m", "3m", "1m",
] as const;

export interface InstrumentBackfillRequest {
  symbol: string;
  exchange: string;
  instrument_type?: string;
  expiry?: string | null;
  strike?: string | null;
  lot_size?: number;
  tick_size?: string;
  /** Omit for all eight. */
  timeframes?: string[] | null;
  start: string;
  end: string;
}

export interface SkippedTimeframe {
  timeframe: string;
  reason: string;
}

export interface InstrumentBackfillResponse {
  /** Coarsest-first, which is also the order they execute in — the runner is
   *  serialised. The 1d job finishing is the cue that the chart can be drawn. */
  jobs: BackfillJob[];
  /** Timeframes no connected broker can serve, each with a reason. */
  skipped: SkippedTimeframe[];
  /** Set only when nothing at all could be scheduled. */
  detail: string | null;
}

/** Fetch an instrument's history from scratch, one job per timeframe.
 *
 *  Use this — NOT `createGapFill` — for a symbol that has no bars yet. Gap-fill
 *  can only top up timeframes that already exist, so on a new symbol it returns
 *  zero jobs and the click appears to do nothing. */
export async function backfillInstrument(
  payload: InstrumentBackfillRequest,
): Promise<InstrumentBackfillResponse> {
  return apiFetch<InstrumentBackfillResponse>("/backfills/instrument", {
    method: "POST",
    body: payload,
  });
}

export async function cancelBackfill(id: string): Promise<BackfillJob> {
  return apiFetch<BackfillJob>(`/backfills/${id}/cancel`, { method: "POST" });
}

export async function retryBackfill(id: string): Promise<BackfillJob> {
  return apiFetch<BackfillJob>(`/backfills/${id}/retry`, { method: "POST" });
}
